import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const months = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const ITEM_HEIGHT = 48;
const VISIBLE_ITEMS = 3;

const SwipeDatePicker = ({
  open,
  onClose,
  value,
  onChange,
  maxDate = new Date(),
}) => {
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const years = Array.from(
    { length: maxDate.getFullYear() - 1899 },
    (_, i) => 1900 + i,
  ).reverse();

  const getDaysInMonth = useCallback((month, year) => {
    return new Date(year, month + 1, 0).getDate();
  }, []);

  const days = Array.from(
    { length: getDaysInMonth(selectedMonth, selectedYear) },
    (_, i) => i + 1,
  );

  useEffect(() => {
    if (value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          setSelectedDay(date.getDate());
          setSelectedMonth(date.getMonth());
          setSelectedYear(date.getFullYear());
        }
      } catch (error) {
        console.error("Invalid date value:", value);
      }
    } else {
      const today = new Date();
      setSelectedDay(today.getDate());
      setSelectedMonth(today.getMonth());
      setSelectedYear(today.getFullYear());
    }
  }, [value]);

  const scrollToIndex = (ref, index) => {
    if (!ref.current) return;

    const scrollTop = index * ITEM_HEIGHT;
    ref.current.scrollTop = scrollTop;
  };

  const handleScroll = (type, ref, items) => {
    if (!ref.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!ref.current) return;

      const scrollTop = ref.current.scrollTop;
      const index = Math.round(scrollTop / ITEM_HEIGHT);

      const targetScroll = index * ITEM_HEIGHT;

      if (Math.abs(ref.current.scrollTop - targetScroll) > 1) {
        ref.current.scrollTo({
          top: targetScroll,
          behavior: "smooth",
        });
      }

      switch (type) {
        case "day":
          if (items[index] && items[index] !== selectedDay) {
            setSelectedDay(items[index]);
          }
          break;
        case "month":
          if (index >= 0 && index < months.length && index !== selectedMonth) {
            setSelectedMonth(index);
          }
          break;
        case "year":
          if (items[index] && items[index] !== selectedYear) {
            setSelectedYear(items[index]);
          }
          break;
        default:
          break;
      }
    }, 150);
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const dayIndex = days.indexOf(selectedDay);
        if (dayIndex >= 0) {
          scrollToIndex(dayRef, dayIndex);
        }

        scrollToIndex(monthRef, selectedMonth);

        const yearIndex = years.indexOf(selectedYear);
        if (yearIndex >= 0) {
          scrollToIndex(yearRef, yearIndex);
        }
      }, 50);
    }
  }, [open]);

  useEffect(() => {
    const maxDay = getDaysInMonth(selectedMonth, selectedYear);
    if (selectedDay > maxDay) {
      setSelectedDay(maxDay);
    }
  }, [selectedMonth, selectedYear, selectedDay, getDaysInMonth]);

  const handleItemClick = (type, value) => {
    switch (type) {
      case "day": {
        setSelectedDay(value);
        const index = days.indexOf(value);
        if (index >= 0) scrollToIndex(dayRef, index);
        break;
      }
      case "month": {
        setSelectedMonth(value);
        scrollToIndex(monthRef, value);
        break;
      }
      case "year": {
        setSelectedYear(value);
        const index = years.indexOf(value);
        if (index >= 0) scrollToIndex(yearRef, index);
        break;
      }
      default:
        break;
    }
  };

  const handleConfirm = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const formattedDate = `${selectedYear}-${String(selectedMonth + 1).padStart(
      2,
      "0",
    )}-${String(selectedDay).padStart(2, "0")}`;

    onChange(formattedDate);
    onClose();
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const renderItems = (items, selectedValue, type) => {
    return (
      <>
        <div style={{ height: `${ITEM_HEIGHT}px` }} />

        {items.map((item, index) => {
          const isSelected =
            type === "day"
              ? item === selectedValue
              : type === "month"
                ? index === selectedValue
                : item === selectedValue;

          const displayValue =
            type === "day"
              ? item.toString().padStart(2, "0")
              : type === "month"
                ? item
                : item;

          return (
            <div
              key={`${type}-${index}`}
              className={`flex items-center justify-center cursor-pointer select-none transition-all duration-200 ${
                isSelected
                  ? "text-black font-medium text-lg"
                  : "text-gray-400 text-base"
              }`}
              style={{ height: `${ITEM_HEIGHT}px` }}
              onClick={() => {
                const clickValue = type === "month" ? index : item;
                handleItemClick(type, clickValue);
              }}
            >
              {displayValue}
            </div>
          );
        })}

        <div style={{ height: `${ITEM_HEIGHT}px` }} />
      </>
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999]"
            onClick={onClose}
          />

          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[9999] max-h-[70vh] flex flex-col shadow-2xl"
          >
            <div className="flex flex-col gap-4 px-4">
              <div className="flex w-full justify-center">
                <div className="bg-gray-200 w-[25%] h-2 rounded-full mt-2"></div>
              </div>
              <h2 className="text-lg font-semibold text-gray-800">
                Pilih Tanggal
              </h2>
            </div>

            <div className="flex-1 overflow-hidden p-4">
              <div
                className="flex relative"
                style={{ height: `${ITEM_HEIGHT * VISIBLE_ITEMS}px` }}
              >
                <div
                  className="absolute left-0 right-0 bg-secondaryActive pointer-events-none z-10 rounded-lg"
                  style={{
                    top: `${ITEM_HEIGHT}px`,
                    height: `${ITEM_HEIGHT}px`,
                  }}
                />

                <div className="flex-1 z-[9999] relative">
                  <div
                    ref={dayRef}
                    className="h-full overflow-y-auto scrollbar-hide"
                    onScroll={() => handleScroll("day", dayRef, days)}
                    style={{
                      scrollSnapType: "y mandatory",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    {renderItems(days, selectedDay, "day")}
                  </div>
                </div>

                <div className="flex-1 z-[9999] relative">
                  <div
                    ref={monthRef}
                    className="h-full overflow-y-auto scrollbar-hide"
                    onScroll={() => handleScroll("month", monthRef, months)}
                    style={{
                      scrollSnapType: "y mandatory",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    {renderItems(months, selectedMonth, "month")}
                  </div>
                </div>

                <div className="flex-1 z-[9999] relative">
                  <div
                    ref={yearRef}
                    className="h-full overflow-y-auto scrollbar-hide"
                    onScroll={() => handleScroll("year", yearRef, years)}
                    style={{
                      scrollSnapType: "y mandatory",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    {renderItems(years, selectedYear, "year")}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50">
              <button
                type="button"
                onClick={(e) => handleConfirm(e)}
                className="h-[45px] flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-success shadow-lg transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
              >
                Simpan
              </button>
            </div>
          </motion.div>

          <style jsx>{`
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
            .scrollbar-hide {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
          `}</style>
        </>
      )}
    </AnimatePresence>
  );
};

export default SwipeDatePicker;
